import Link from "next/link";

import authors from "@/content/resources/authors";

import type { ResourcePostMeta } from "@/lib/resources/schema";

interface ResourcePostGridProps {
  posts: ResourcePostMeta[];
}

export default function ResourcePostGrid({ posts }: ResourcePostGridProps) {
  return (
    <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
      {posts.map((post) => {
        const author = authors[post.authorKey];
        return (
          <article key={post.slug} className="flex flex-col items-start">
            <Link className="relative w-full hover:opacity-80 " href={`/resources/${post.slug}`}>
              <img
                alt={post.featuredImageAlt}
                src={post.featuredImage}
                className="aspect-video w-full rounded-2xl bg-ash-100 object-cover sm:aspect-2/1 lg:aspect-3/2"
              />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-ash-900/10" />
            </Link>
            <div className="max-w-xl">
              <div className="mt-8 flex items-center gap-x-4 text-xs">
                <time suppressHydrationWarning dateTime={post.updatedAt} className="text-ash-500">
                  {new Date(post.updatedAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
                {post.tags[0] && (
                  <span className="relative z-10 rounded-full bg-ash-100 px-3 py-1.5 font-medium text-ash-600">
                    {post.tags[0]}
                  </span>
                )}
              </div>
              <div className="group relative">
                <h3 className="mt-3 text-lg font-semibold leading-6 text-ash-900 group-hover:text-ash-600">
                  <Link href={`/resources/${post.slug}`}>
                    <span className="absolute inset-0" />
                    {post.title}
                  </Link>
                </h3>
                {post.shortDescription && (
                  <p className="mt-2 line-clamp-3 text-sm leading-6 text-ash-600">{post.shortDescription}</p>
                )}
              </div>
              <div className="relative mt-8 flex items-center gap-x-4">
                {author?.avatarUrl && (
                  <img
                    alt={`${author.name} photo`}
                    src={author.avatarUrl}
                    className="h-10 w-10 rounded-full bg-ash-100"
                  />
                )}
                <div className="text-sm leading-6">
                  <p className="font-semibold text-ash-900">
                    <span>{author?.displayName ?? author?.name ?? post.authorKey}</span>
                  </p>
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
